'use client';

import { type AriaButtonOptions, useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeRefs } from '@react-aria/utils';
import { type ElementType, useMemo, useRef } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { filterProps } from '../../internal/utils/filterProps';
import { focusRingStyles } from '../../styles/focusRing';
import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import {
  ListContext,
  type ListContextValue,
  ListItemButtonContext,
  useListContext,
} from './ListContext';

const ELEMENT_TYPE = 'button';

const listItemButtonClasses = { root: 'zds-list-item-button-root' } as const;

const listItemButtonStyles = tv({
  base: [
    listItemButtonClasses.root,
    // Reset native button defaults
    'appearance-none border-0 bg-transparent p-0 text-left text-[inherit] no-underline',
    'box-border flex w-full gap-4 px-4',
    'cursor-pointer',
    // Disable 300ms tap delay on touch devices
    'touch-manipulation',
    // Remove iOS/Android tap highlight flash so press styles are the only feedback
    '[-webkit-tap-highlight-color:transparent]',
  ],
  variants: {
    density: {
      compact: 'py-1',
      default: 'py-2',
      spacious: 'py-3',
    },
    densityXs: {
      compact: 'xs:py-1',
      default: 'xs:py-2',
      spacious: 'xs:py-3',
    },
    densitySm: {
      compact: 'sm:py-1',
      default: 'sm:py-2',
      spacious: 'sm:py-3',
    },
    densityMd: {
      compact: 'md:py-1',
      default: 'md:py-2',
      spacious: 'md:py-3',
    },
    densityLg: {
      compact: 'lg:py-1',
      default: 'lg:py-2',
      spacious: 'lg:py-3',
    },
    densityXl: {
      compact: 'xl:py-1',
      default: 'xl:py-2',
      spacious: 'xl:py-3',
    },
    alignItems: {
      top: 'items-start',
      center: 'items-center',
    },
    color: {
      neutral: [],
      'accent-primary': [],
      'static-white': ['text-static-white-main'],
    },
    shape: {
      square: {},
      rounded: 'z--list-list-item-rounded-border-radius',
    },
    isSelected: {
      true: '',
    },
    isDisabled: {
      true: 'cursor-not-allowed disabled-foreground',
    },
  },
  compoundVariants: [
    {
      color: 'neutral',
      isSelected: false,
      class: [
        'hover:not-disabled:bg-neutral-muted-5-blank-hovered',
        'pressed:not-disabled:bg-neutral-muted-5-blank-pressed',
      ],
    },
    {
      color: 'accent-primary',
      isSelected: false,
      class: [
        'hover:not-disabled:bg-neutral-muted-5-blank-hovered',
        'pressed:not-disabled:bg-neutral-muted-5-blank-pressed',
      ],
    },
    {
      color: 'static-white',
      isSelected: false,
      class: [
        'hover:not-disabled:bg-static-white-main-blank-hovered',
        'pressed:not-disabled:bg-static-white-main-blank-pressed',
      ],
    },
    {
      color: 'neutral',
      isSelected: true,
      class: 'bg-neutral-muted-5-blank-pressed',
    },
    {
      color: 'accent-primary',
      isSelected: true,
      class: [
        'bg-accent-primary-main-blank-pressed',
        'text-accent-primary-main',
      ],
    },
    {
      color: 'static-white',
      isSelected: true,
      class: 'bg-static-white-main-blank-pressed',
    },
  ],
});

type Variant = VariantProps<typeof listItemButtonStyles>;

interface ListItemButtonBaseProps {
  /** Vertical alignment of children along the row. Inherits from `List` context, falls back to `'top'`. */
  alignItems?: 'top' | 'center';
  /** Override colour from context for this button and its descendants. */
  color?: Variant['color'];
  /** Corner shape. Inherits from `List` context, falls back to `'square'`. */
  shape?: Variant['shape'];
  /**
   * Visually marks the item as selected or current.
   *
   * For native button elements, sets `aria-pressed={true}` automatically.
   * For link elements (`component="a"` or router links with `href`/`to`), no ARIA
   * attribute is set — add the appropriate `aria-current` value yourself
   * (e.g. `aria-current="page"` for current-page navigation).
   * @default false
   */
  isSelected?: boolean;
  /**
   * Prevents interaction.
   * @default false
   */
  isDisabled?: boolean;
}

type ListItemButtonProps<
  RootComponent extends React.ElementType = typeof ELEMENT_TYPE,
> = PolymorphicComponentProps<ListItemButtonBaseProps, RootComponent>;

type ListItemButtonComponent = PolymorphicComponent<
  ListItemButtonBaseProps,
  typeof ELEMENT_TYPE
>;

const ListItemButton: ListItemButtonComponent = (props) => {
  const Root: ElementType =
    (props as ListItemButtonProps<ElementType>).component ?? ELEMENT_TYPE;

  const [localProps, buttonRaProps, hoverRaProps] = filterProps(props, [
    'useButton',
    'useHover',
  ]);

  const {
    children,
    component: _component,
    alignItems: alignItemsProp,
    color: colorProp,
    shape: shapeProp,
    isSelected = false,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  const { isDisabled = false } = buttonRaProps;

  const context = useListContext();
  const density = context?.density ?? 'default';
  const responsiveDensity = context?.responsiveDensity;
  const alignItems = alignItemsProp ?? context?.alignItems ?? 'center';
  const color = colorProp ?? context?.color ?? 'neutral';
  const shape = shapeProp ?? context?.shape ?? 'square';

  const itemContext = useMemo<ListContextValue>(
    () => ({ density, responsiveDensity, color, alignItems, shape }),
    [density, responsiveDensity, color, alignItems, shape]
  );

  const buttonContext = useMemo(
    () => ({ isDisabled, isSelected }),
    [isDisabled, isSelected]
  );

  const buttonRef = useRef<HTMLButtonElement>(null);

  // Custom components with href/to get anchor semantics so useButton
  // correctly handles href stripping on disable, aria-disabled, etc.
  const resolvedElementType =
    typeof Root !== 'string' &&
    ('to' in restLocalProps || 'href' in buttonRaProps)
      ? 'a'
      : Root;

  const { buttonProps: rawButtonProps, isPressed } = useButton(
    {
      ...(buttonRaProps as AriaButtonOptions<ElementType>),
      isDisabled,
      elementType: resolvedElementType,
    },
    buttonRef
  );

  // useButton adds role="button" to all non-<button> elements. Strip it for
  // anchors which have their own implicit "link" role.
  const { role: _role, ...anchorButtonProps } = rawButtonProps;
  const buttonProps =
    resolvedElementType === 'a' ? anchorButtonProps : rawButtonProps;

  const isLink = resolvedElementType === 'a';
  const hasAriaCurrent =
    'aria-current' in props && props['aria-current'] != null;
  const shouldSetAriaPressed = isSelected && !isLink && !hasAriaCurrent;

  const { hoverProps, isHovered } = useHover({ ...hoverRaProps, isDisabled });
  const { focusProps, isFocusVisible } = useFocusRing();

  const focusStyles = focusRingStyles({
    color: color === 'static-white' ? 'static-white' : 'default',
    position: 'inner',
  });

  return (
    <ListContext.Provider value={itemContext}>
      <ListItemButtonContext.Provider value={buttonContext}>
        <Root
          {...mergeProps(restLocalProps, buttonProps, hoverProps, focusProps)}
          ref={mergeRefs(buttonRef, ref)}
          data-hovered={isHovered || undefined}
          data-focus-visible={isFocusVisible || undefined}
          data-pressed={isPressed || undefined}
          aria-pressed={shouldSetAriaPressed ? true : undefined}
          data-disabled={isDisabled || undefined}
          className={focusStyles.root({
            className: listItemButtonStyles({
              density,
              densityXs: responsiveDensity?.xs,
              densitySm: responsiveDensity?.sm,
              densityMd: responsiveDensity?.md,
              densityLg: responsiveDensity?.lg,
              densityXl: responsiveDensity?.xl,
              alignItems,
              color,
              shape,
              isSelected,
              isDisabled,
              className,
            }),
          })}
        >
          {children}
        </Root>
      </ListItemButtonContext.Provider>
    </ListContext.Provider>
  );
};

ListItemButton.displayName = 'List.ItemButton';

export {
  ListItemButton,
  type ListItemButtonProps,
  type ListItemButtonBaseProps,
  listItemButtonClasses,
};
